import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import enCommon from "./locales/en/common.json";
import enSmartImport from "./locales/en/smartImport.json";
import enImports from "./locales/en/imports.json";
import enPim from "./locales/en/pim.json";

import deCommon from "./locales/de/common.json";
import deSmartImport from "./locales/de/smartImport.json";
import deImports from "./locales/de/imports.json";
import dePim from "./locales/de/pim.json";

const enableDebug = import.meta.env.VITE_IS_DEBUG;

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    debug: enableDebug,
    fallbackLng: "en",
    defaultNS: "common",
    interpolation: {
      escapeValue: false,
    },
    react: {
      wait: false,
      withRef: false,
      bindI18n: "languageChanged loaded",
      bindStore: "added removed",
      nsMode: "default",
    },
    resources: {
      en: {
        common: enCommon,
        smartImport: enSmartImport,
        imports: enImports,
        pim: enPim,
        // Kept inline for Boilerplate component compat
        ImportStatus: {
          pending: "Pending",
          processing: "Processing",
          unmapped: "Unmapped",
          mapped: "Mapped",
          succeeded: "Succeeded",
          completedWithErrors: "Completed with errors",
          failed: "Failed",
          unknown: "Unknown",
        },
        FIDO: {
          fidoKey: {
            registeredSuccess: "Passkey registered successfully",
            keyAdded: "Your passkey has been added",
            fetchError: "Error fetching passkeys",
            deletedSuccess: "Passkey deleted successfully",
            keyRemoved: "Passkey removed",
            deleteError: "Error deleting passkey",
            lastUsed: "Last used",
            created: "created",
            actions: {
              add: "Add passkey",
              remove: "Remove passkey",
            },
            title_passkeys: "Passkeys",
          },
          registerError: "Error registering passkey",
        },
        Auth: {
          name_placeholder: "John Doe",
          email_placeholder: "doe@example.com",
          register: "Register",
          name: "Name",
          current_password: "Current password",
          new_password: "New password",
          change_password: "Change password",
          title: "Login to FacetFlux",
          invalid_email: "Invalid email",
          invalid_password: "Password should include at least 8 characters",
          login_failed: "Login failed",
          incorrect_user_or_password: "Incorrect user or password",
          rate_limit_exceeded:
            "Too many login attempts. Please try again later.",
          unexpected_error: "An unexpected error has occurred: ",
          email: "Email",
          password: "Password",
          your_password: "Your password",
          forgot_password: "Forgot password?",
          login: "Login",
          you_are_logged_in: "You are logged in",
          password_reset_requested: "Password reset requested",
          password_reset_requested_message:
            "Please check your email and follow the reset link to set a new password.",
          recover_password: "Recover password",
          request_reset_email: "Send recovery email",
          login_instead: "Login instead",
          // Signup, activation, invitation, reset (shared Boilerplate forms).
          register_title: "Create your free account",
          register_failed: "Registration failed",
          or_continue_with_email: "Or continue with email",
          register_rate_limited: "Too many attempts. Please wait a minute and try again.",
          register_error: "We could not create your account. Please try again in a moment.",
          accept_terms_prefix: "I accept the",
          terms_link: "terms and conditions",
          have_account_login: "Already have an account? Log in",
          register_success_title: "Check your inbox",
          register_success_message:
            "We sent an activation link to {{email}}. Open it to set your password and start.",
          register_success_hint:
            "No mail after a few minutes? Check your spam folder. Or submit the form again with the same address to get a new link.",
          register_use_other_email: "Use a different address",
          sign_in_with_passkey: "Sign in with Passkey",
          passkey_required_login:
            "Your organization requires passkey sign-in. Please sign in with your passkey.",
          enter_your_email: "Enter your email",
          continue: "Continue",
          no_account_register: "No account yet? Sign up for free",
          activation_failed: "Account activation failed",
          request_new_link: "Send me a new link",
          activation_title: "Set a password for your account",
          activation_message:
            "Choose a password to activate your account. You can use it to log in from now on.",
          activation_link_invalid:
            "This activation link has expired or is not valid. Request a new link with your email address. If you already activated your account, log in instead.",
          activation_rate_limited: "Too many attempts. Please wait a minute and try again.",
          activation_error: "We could not activate your account. Please try again in a moment.",
          choose_password: "Choose a password (at least 8 characters)",
          activate_account: "Activate account",
          invitation_not_found_title: "Invitation not found",
          invitation_not_found_message:
            "This invitation link is not valid. Ask the person who invited you to send a new invitation. If you already accepted it, log in instead.",
          invitation_failed: "Could not accept the invitation",
          invitation_intro:
            "{{inviter}} invited you to join their workspace on {{appName}}. Set a password to accept.",
          or_set_password: "Or set a password to log in with email",
          invitation_expired_title: "Invitation expired",
          invitation_expired_message:
            "This invitation is no longer valid. Invitations are valid for 48 hours. Ask the person who invited you to send a new one.",
          invitation_accepted_title: "Invitation already accepted",
          invitation_accepted_message:
            "This invitation was already used. Log in with your email address and password.",
          invitation_error: "This did not work. Please try again in a moment.",
          accept_invitation: "Accept invitation",
          reset_failed: "Reset failed",
          set_new_password: "Set a new password",
          password_saved: "Password saved",
          password_saved_message: "Log in with your new password.",
          reset_link_invalid:
            "This link has expired or is not valid. Request a new one on the password recovery page.",
          reset_error: "We could not save your password. Please try again in a moment.",
          save_password: "Save password",
        },
        Users: {
          add_user: "Add user",
          email: "Email",
          first_name: "First name",
          last_name: "Last name",
          role: "Role",
          created: "Created",
          is_active: "Active",
          user_deleted: "User deleted!",
          invite_user: "Invite user",
          invitation_sent: "Invitation sent!",
          invite_user_info:
            "This will send an invitation to the user so they can join you to collaborate. Users can choose to login via a different login than email, but an email is required for the invitation.",
          save: "Save",
          user_exists: "This address already has an account",
          user_exists_message:
            "This person has an account already, possibly in another workspace. Ask them to log in, or use a different address.",
        },
        // Legacy namespace — common.json now carries these keys too,
        // but existing Boilerplate components still reference "translation:..."
        translation: enCommon,
      },
      de: {
        common: deCommon,
        smartImport: deSmartImport,
        imports: deImports,
        pim: dePim,
        ImportStatus: {
          pending: "Ausstehend",
          processing: "Wird verarbeitet",
          unmapped: "Nicht zugeordnet",
          mapped: "Zugeordnet",
          succeeded: "Erfolgreich",
          completedWithErrors: "Mit Fehlern abgeschlossen",
          failed: "Fehlgeschlagen",
          unknown: "Unbekannt",
        },
        FIDO: {
          fidoKey: {
            registeredSuccess: "Passkey erfolgreich registriert",
            keyAdded: "Ihr Passkey wurde hinzugefügt",
            fetchError: "Fehler beim Abrufen der Passkeys",
            deletedSuccess: "Passkey erfolgreich gelöscht",
            keyRemoved: "Passkey entfernt",
            deleteError: "Fehler beim Löschen des Passkeys",
            lastUsed: "Zuletzt verwendet",
            created: "erstellt",
            actions: {
              add: "Passkey hinzufügen",
              remove: "Passkey entfernen",
            },
            title_passkeys: "Passkeys",
          },
          registerError: "Fehler bei der Passkey-Registrierung",
        },
        Auth: {
          name_placeholder: "Max Mustermann",
          email_placeholder: "max@beispiel.de",
          register: "Registrieren",
          name: "Name",
          current_password: "Aktuelles Passwort",
          new_password: "Neues Passwort",
          change_password: "Passwort ändern",
          title: "Bei FacetFlux anmelden",
          invalid_email: "Ungültige E-Mail-Adresse",
          invalid_password: "Das Passwort muss mindestens 8 Zeichen enthalten",
          login_failed: "Anmeldung fehlgeschlagen",
          incorrect_user_or_password: "Falscher Benutzername oder falsches Passwort",
          rate_limit_exceeded:
            "Zu viele Anmeldeversuche. Bitte versuchen Sie es später erneut.",
          unexpected_error: "Ein unerwarteter Fehler ist aufgetreten: ",
          email: "E-Mail",
          password: "Passwort",
          your_password: "Ihr Passwort",
          forgot_password: "Passwort vergessen?",
          login: "Anmelden",
          you_are_logged_in: "Sie sind angemeldet",
          password_reset_requested: "Passwort-Zurücksetzung angefordert",
          password_reset_requested_message:
            "Bitte überprüfen Sie Ihre E-Mails und folgen Sie dem Link zum Zurücksetzen Ihres Passworts.",
          recover_password: "Passwort wiederherstellen",
          request_reset_email: "Wiederherstellungs-E-Mail senden",
          login_instead: "Stattdessen anmelden",
          // Registrierung, Aktivierung, Einladung, Zurücksetzen (gemeinsame Boilerplate-Formulare).
          register_title: "Kostenloses Konto erstellen",
          register_failed: "Registrierung fehlgeschlagen",
          or_continue_with_email: "Oder weiter mit E-Mail",
          register_rate_limited:
            "Zu viele Versuche. Bitte warten Sie eine Minute und versuchen Sie es dann erneut.",
          register_error:
            "Ihr Konto konnte nicht angelegt werden. Bitte versuchen Sie es gleich noch einmal.",
          accept_terms_prefix: "Ich akzeptiere die",
          terms_link: "Nutzungsbedingungen",
          have_account_login: "Sie haben schon ein Konto? Anmelden",
          register_success_title: "Bitte prüfen Sie Ihr Postfach",
          register_success_message:
            "Wir haben einen Aktivierungslink an {{email}} gesendet. Öffnen Sie ihn, um Ihr Passwort festzulegen und loszulegen.",
          register_success_hint:
            "Nach einigen Minuten keine E-Mail? Prüfen Sie Ihren Spam-Ordner. Oder senden Sie das Formular mit derselben Adresse noch einmal ab. Dann erhalten Sie einen neuen Link.",
          register_use_other_email: "Andere Adresse verwenden",
          sign_in_with_passkey: "Mit Passkey anmelden",
          passkey_required_login:
            "Ihre Organisation verlangt die Anmeldung per Passkey. Bitte melden Sie sich mit Ihrem Passkey an.",
          enter_your_email: "Ihre E-Mail-Adresse",
          continue: "Weiter",
          no_account_register: "Noch kein Konto? Kostenlos registrieren",
          activation_failed: "Aktivierung fehlgeschlagen",
          request_new_link: "Neuen Link anfordern",
          activation_title: "Passwort für Ihr Konto festlegen",
          activation_message:
            "Wählen Sie ein Passwort, um Ihr Konto zu aktivieren. Damit melden Sie sich künftig an.",
          activation_link_invalid:
            "Dieser Aktivierungslink ist abgelaufen oder ungültig. Fordern Sie mit Ihrer E-Mail-Adresse einen neuen Link an. Wenn Ihr Konto schon aktiv ist, melden Sie sich an.",
          activation_rate_limited:
            "Zu viele Versuche. Bitte warten Sie eine Minute und versuchen Sie es dann erneut.",
          activation_error:
            "Ihr Konto konnte nicht aktiviert werden. Bitte versuchen Sie es gleich noch einmal.",
          choose_password: "Passwort wählen (mindestens 8 Zeichen)",
          activate_account: "Konto aktivieren",
          invitation_not_found_title: "Einladung nicht gefunden",
          invitation_not_found_message:
            "Dieser Einladungslink ist ungültig. Bitten Sie die einladende Person um eine neue Einladung. Wenn Sie die Einladung schon angenommen haben, melden Sie sich an.",
          invitation_failed: "Einladung konnte nicht angenommen werden",
          invitation_intro:
            "{{inviter}} hat Sie in einen Arbeitsbereich auf {{appName}} eingeladen. Legen Sie ein Passwort fest, um die Einladung anzunehmen.",
          or_set_password: "Oder Passwort für die Anmeldung per E-Mail festlegen",
          invitation_expired_title: "Einladung abgelaufen",
          invitation_expired_message:
            "Diese Einladung ist nicht mehr gültig. Einladungen gelten 48 Stunden. Bitten Sie die einladende Person um eine neue Einladung.",
          invitation_accepted_title: "Einladung bereits angenommen",
          invitation_accepted_message:
            "Diese Einladung wurde schon verwendet. Melden Sie sich mit Ihrer E-Mail-Adresse und Ihrem Passwort an.",
          invitation_error:
            "Das hat nicht funktioniert. Bitte versuchen Sie es gleich noch einmal.",
          accept_invitation: "Einladung annehmen",
          reset_failed: "Zurücksetzen fehlgeschlagen",
          set_new_password: "Neues Passwort festlegen",
          password_saved: "Passwort gespeichert",
          password_saved_message: "Melden Sie sich mit Ihrem neuen Passwort an.",
          reset_link_invalid:
            "Dieser Link ist abgelaufen oder ungültig. Fordern Sie auf der Seite „Passwort wiederherstellen“ einen neuen Link an.",
          reset_error:
            "Ihr Passwort konnte nicht gespeichert werden. Bitte versuchen Sie es gleich noch einmal.",
          save_password: "Passwort speichern",
        },
        Users: {
          add_user: "Benutzer hinzufügen",
          email: "E-Mail",
          first_name: "Vorname",
          last_name: "Nachname",
          role: "Rolle",
          created: "Erstellt",
          is_active: "Aktiv",
          user_deleted: "Benutzer gelöscht!",
          invite_user: "Benutzer einladen",
          invitation_sent: "Einladung gesendet!",
          invite_user_info:
            "Hiermit wird eine Einladung an den Benutzer gesendet, damit er mit Ihnen zusammenarbeiten kann. Benutzer können sich auch über andere Methoden als E-Mail anmelden, aber eine E-Mail-Adresse ist für die Einladung erforderlich.",
          save: "Speichern",
          user_exists: "Für diese Adresse gibt es schon ein Konto",
          user_exists_message:
            "Diese Person hat bereits ein Konto, möglicherweise in einem anderen Arbeitsbereich. Bitten Sie sie, sich anzumelden, oder verwenden Sie eine andere Adresse.",
        },
        translation: deCommon,
      },
    },
  });

export default i18n;
